import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchState, fetchDistrict, fetchTaluka } from "../../../api/locationApi";
import { useAuth } from "../../../sections/auth/AuthContext";
import { toast } from "react-toastify";
import { addDistributor } from "../../../api/distributorApi";

export default function DistributorForm() {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // -------------------------------
  // FIX: SAFE snake_case → camelCase mapping
  // -------------------------------
  const apiDistributor = location.state?.distributor || null;

  const existing = apiDistributor
    ? {
        DistributorCode: apiDistributor.distributor_code,
        DistributorName: apiDistributor.distributor_name,
        FirmName: apiDistributor.firm_name,
        GstNo: apiDistributor.gst_no,
        PanNo: apiDistributor.pan_no,
        MobileNumber: apiDistributor.mobile_number,
        EmailId: apiDistributor.email_id,
        State: apiDistributor.state ? { description: apiDistributor.state } : null,
        District: apiDistributor.district ? { description: apiDistributor.district } : null,
        Taluka: apiDistributor.taluka ? { description: apiDistributor.taluka } : null,
        Village: apiDistributor.village,
        Address: apiDistributor.address,
      }
    : null;

  const [formData, setFormData] = useState({
    DistributorCode: existing?.DistributorCode || "",
    DistributorName: existing?.DistributorName || "",
    FirmName: existing?.FirmName || "",
    GstNo: existing?.GstNo || "",
    PanNo: existing?.PanNo || "",
    MobileNumber: existing?.MobileNumber || "",
    EmailId: existing?.EmailId || "",
    State: existing?.State || null,
    District: existing?.District || null,
    Taluka: existing?.Taluka || null,
    Village: existing?.Village || "",
    Address: existing?.Address || "",
    Usercode: userInfo?.empCode || "",
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------------------------------
  // LOAD STATE → DISTRICT → TALUKA FOR EDIT MODE
  // -------------------------------
  useEffect(() => {
    (async () => {
      const stateList = await fetchState();
      setStates(stateList);

      if (existing?.State?.description) {
        const stateRow = stateList.find((s) => s.description === existing.State.description);

        if (stateRow) {
          const ds = await fetchDistrict(stateRow.code);
          setDistricts(ds);

          if (existing?.District?.description) {
            const districtRow = ds.find((d) => d.description === existing.District.description);

            if (districtRow) {
              const ts = await fetchTaluka(districtRow.code);
              setTalukas(ts);
            }
          }
        }
      }
    })();
  }, []);

  // -------------------------------
  // LOAD DISTRICT & TALUKA OPTIONS
  // -------------------------------
  const loadDistricts = async (stateCode) => {
    const ds = await fetchDistrict(stateCode);
    setDistricts(ds);
    setTalukas([]);
  };

  const loadTalukas = async (distCode) => {
    const ts = await fetchTaluka(distCode);
    setTalukas(ts);
  };

  // -------------------------------
  // VALIDATION
  // -------------------------------
    const validate = () => {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

      if (!formData.DistributorName) {
          toast.error("Distributor name is required");
          return false;
      }

      if (!formData.FirmName) {
          toast.error("Firm name is required");
          return false;
      }

      if (!formData.PanNo) {
          toast.error("PAN number is required");
          return false;
      }

      if (formData.PanNo.length !== 10) {
          toast.error("PAN number must be 10 characters");
          return false;
      }

      if (!panRegex.test(formData.PanNo)) {
          toast.error("Invalid PAN number");
          return false;
      }

      if (!formData.MobileNumber || formData.MobileNumber.length !== 10) {
          toast.error("Mobile number must be 10 digits");
          return false;
      }

      if (formData.EmailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.EmailId)) {
          toast.error("Invalid email");
          return false;
      }

      if (!formData.State) {
          toast.error("State is required");
          return false;
      }

      if (!formData.District) {
          toast.error("District is required");
          return false;
      }

      if (!formData.Taluka) {
          toast.error("Taluka is required");
          return false;
      }

      if (!formData.Village) {
          toast.error("Village is required");
          return false;
      }

      if (!formData.Address) {
          toast.error("Address is required");
          return false;
      }

      return true;
  };


  // -------------------------------
  // SUBMIT
  // -------------------------------
  const handleSubmit = async () => {
    if (!validate()) return;

    const key = formData.DistributorCode ? "UPDATE" : "INSERT";

    const payload = {
      Key: key,
      DistributorCode: formData.DistributorCode || undefined,
      DistributorName: formData.DistributorName,
      FirmName: formData.FirmName,
      GstNo: formData.GstNo,
      PanNo: formData.PanNo,
      MobileNumber: formData.MobileNumber,
      EmailId: formData.EmailId,
      State: formData.State?.description || "",
      District: formData.District?.description || "",
      Taluka: formData.Taluka?.description || "",
      Village: formData.Village,
      Address: formData.Address,
      Usercode: formData.Usercode,
    };

    try {
      setLoading(true);
      const res = await addDistributor(payload);

      const message =
        res?.insert_update_distributor_master?.message ||
        res?.data?.[0]?.insert_update_distributor_master?.message ||
        res?.message;

      if (message?.includes("successfully")) {
        toast.success(message);
        navigate("/distributor-master");
      } else {
        toast.error(message || "Unknown response from server");
      }
    } catch (err) {
      toast.error("Error while saving distributor");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // STYLE (UNCHANGED)
  // -------------------------------
  const sharedInputSx = {
    width: "200px",
    "& .MuiOutlinedInput-root": {
      minHeight: 52,
      borderRadius: 2,
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 12px",
      background: "#fff",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "calc(50% - 12px)",
    },
  };

  return (
    <Box p={3} display="flex" justifyContent="center" sx={{ background: "#f5f6fa" }}>
      <Paper elevation={5} sx={{ p: 4, width: "85%", borderRadius: 4, background: "#ffffff" }}>
        <Typography variant="h4" fontWeight={700} mb={3}>
          Distributor {formData.DistributorCode ? "— Edit" : "— Add"}
        </Typography>

        {/* BASIC */}
        <Box mb={2}>
          <Typography variant="h6" fontWeight={600}>Basic Details</Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth
              label="Distributor Name"
              InputLabelProps={{ shrink: true }}
              value={formData.DistributorName}
              onChange={(e) => setFormData({ ...formData, DistributorName: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth
              label="Firm Name"
              InputLabelProps={{ shrink: true }}
              value={formData.FirmName}
              onChange={(e) => setFormData({ ...formData, FirmName: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="GST No"
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 15 }}
              value={formData.GstNo}
              onChange={(e) =>
                setFormData({ ...formData, GstNo: e.target.value.toUpperCase() })
              }
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              required fullWidth
              label="PAN No"
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 10 }}
              value={formData.PanNo}
              onChange={(e) =>
                setFormData({ ...formData, PanNo: e.target.value.toUpperCase() })
              }
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Email"
              InputLabelProps={{ shrink: true }}
              value={formData.EmailId}
              onChange={(e) => setFormData({ ...formData, EmailId: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth
              label="Mobile Number"
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 10 }}
              value={formData.MobileNumber}
              onChange={(e) => setFormData({ ...formData, MobileNumber: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>
        </Grid>

        {/* LOCATION */}
        <Box mt={4} mb={2}>
          <Typography variant="h6" fontWeight={600}>Location Details</Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              fullWidth
              options={states}
              value={formData.State}
              onChange={(e, val) => {
                setFormData({ ...formData, State: val, District: null, Taluka: null });
                if (val) loadDistricts(val.code);
              }}
              getOptionLabel={(o) => o?.description || ""}
              renderInput={(params) => (
                <TextField {...params} required label="State" InputLabelProps={{ shrink: true }} sx={sharedInputSx} />
              )}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              fullWidth
              options={districts}
              value={formData.District}
              onChange={(e, val) => {
                setFormData({ ...formData, District: val, Taluka: null });
                if (val) loadTalukas(val.code);
              }}
              getOptionLabel={(o) => o?.description || ""}
              renderInput={(params) => (
                <TextField {...params} required label="District" InputLabelProps={{ shrink: true }} sx={sharedInputSx} />
              )}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              fullWidth
              options={talukas}
              value={formData.Taluka}
              onChange={(e, val) => setFormData({ ...formData, Taluka: val })}
              getOptionLabel={(o) => o?.description || ""}
              renderInput={(params) => (
                <TextField {...params} required label="Taluka" InputLabelProps={{ shrink: true }} sx={sharedInputSx} />
              )}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth
              label="Village"
              InputLabelProps={{ shrink: true }}
              value={formData.Village}
              onChange={(e) => setFormData({ ...formData, Village: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth
              label="Address"
              InputLabelProps={{ shrink: true }}
              value={formData.Address}
              onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
              sx={sharedInputSx}
            />
          </Grid>
        </Grid>

        {/* BUTTONS */}
        <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
          <Button variant="outlined" size="large" onClick={() => navigate("/distributor-master")}>
            Cancel
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : null}
            sx={{ px: 4, py: 1.5 }}
          >
            {formData.DistributorCode ? "Update" : "Create"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
